import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createRecruitingNote } from '../../../../services/recruiting-notes';
import { getProspect, getProspects } from '../../../../services/prospects';
import type { ProspectResponse } from '../../../../types/api';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import { NotifyStaffModal } from '../../../../components/NotifyStaffModal';
import './CreateRecruitingNotePage.css';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_RESULTS = 10;

type SelectedProspect = {
  id: string;
  firstName: string;
  lastName: string;
};

const formatProspectName = (p: Pick<ProspectResponse, 'firstName' | 'lastName'>) =>
  `${p.firstName} ${p.lastName}`.trim();

/**
 * Web text-only recruiting note create form. Reachable from:
 *  - PlayersTable notes button (with ?prospectId= prefilled)
 *  - Latest Notes feed "+ New Note" button (no prospect prefill — uses picker)
 *
 * Handwritten capture is intentionally mobile-only.
 */
export const CreateRecruitingNotePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prospectIdParam = searchParams.get('prospectId');

  const [selected, setSelected] = useState<SelectedProspect | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProspectResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<{ id: string; prospect: SelectedProspect } | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);

  // Pre-fill from ?prospectId= so the picker step is skipped when the user
  // launched the form from a player row.
  useEffect(() => {
    if (!prospectIdParam) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    getProspect(prospectIdParam)
      .then(prospect => {
        if (cancelled) return;
        setSelected({
          id: prospect.id,
          firstName: prospect.firstName,
          lastName: prospect.lastName,
        });
      })
      .catch(err => {
        if (cancelled) return;
        setPrefillError(
          getSafeErrorMessage(err, 'Failed to load prospect — please pick one manually'),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [prospectIdParam]);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchQuery(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Search prospects when query changes (and no prospect already selected)
  const searchRequestId = useRef(0);
  useEffect(() => {
    if (selected) {
      setSearchResults([]);
      return;
    }
    const trimmed = debouncedSearchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    const requestId = ++searchRequestId.current;
    setSearching(true);
    setSearchError(null);
    getProspects({ searchTerm: trimmed, pageSize: MAX_RESULTS })
      .then(response => {
        if (requestId !== searchRequestId.current) return;
        setSearchResults(response.items);
      })
      .catch(err => {
        if (requestId !== searchRequestId.current) return;
        setSearchError(getSafeErrorMessage(err, 'Failed to search prospects'));
      })
      .finally(() => {
        if (requestId === searchRequestId.current) {
          setSearching(false);
        }
      });
  }, [debouncedSearchQuery, selected]);

  const handleClearSelected = useCallback(() => {
    setSelected(null);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  const handleSelectProspect = useCallback((prospect: ProspectResponse) => {
    setSelected({
      id: prospect.id,
      firstName: prospect.firstName,
      lastName: prospect.lastName,
    });
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  const canSubmit = useMemo(
    () => content.trim().length > 0 && !submitting,
    [content, submitting],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!canSubmit) return;

      setSubmitting(true);
      setSubmitError(null);
      try {
        const created = await createRecruitingNote({
          content: content.trim(),
          title: title.trim() || undefined,
          prospectId: selected?.id,
        });
        // A note on a player can be tagged, so offer that before leaving. `submitting` stays
        // true, which keeps Save disabled and a second copy from being written meanwhile.
        if (selected) {
          setSavedNote({ id: created.id, prospect: selected });
        } else {
          navigate('/latest-notes');
        }
      } catch (err) {
        setSubmitError(getSafeErrorMessage(err, 'Failed to save note'));
        setSubmitting(false);
      }
    },
    [canSubmit, content, navigate, selected, title],
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Send the user back to the most useful surface for what they just did.
  const leaveAfterSave = useCallback(
    (prospect: SelectedProspect) => {
      navigate(`/players?prospectId=${prospect.id}&section=notes`);
    },
    [navigate],
  );

  return (
    <div className="create-recruiting-note">
      <div className="create-recruiting-note__header">
        <h1 className="create-recruiting-note__title">New Recruiting Note</h1>
        <button
          type="button"
          className="create-recruiting-note__cancel"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>

      {prefillError && (
        <div className="create-recruiting-note__error">{prefillError}</div>
      )}

      <form className="create-recruiting-note__form" onSubmit={handleSubmit}>
        <label className="create-recruiting-note__field">
          <span className="create-recruiting-note__field-label">Prospect</span>
          {selected ? (
            <div className="create-recruiting-note__selected">
              <span className="create-recruiting-note__selected-name">
                {formatProspectName(selected)}
              </span>
              <button
                type="button"
                className="create-recruiting-note__selected-clear"
                onClick={handleClearSelected}
                disabled={submitting}
              >
                Change
              </button>
            </div>
          ) : (
            <div className="create-recruiting-note__picker">
              <input
                type="text"
                className="create-recruiting-note__search-input"
                placeholder="Search prospects by name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                disabled={submitting}
                autoFocus
              />
              {searchError && (
                <div className="create-recruiting-note__error">{searchError}</div>
              )}
              {searching && (
                <div className="create-recruiting-note__searching">Searching...</div>
              )}
              {!searching && searchResults.length > 0 && (
                <ul className="create-recruiting-note__results">
                  {searchResults.map(prospect => (
                    <li key={prospect.id}>
                      <button
                        type="button"
                        className="create-recruiting-note__result"
                        onClick={() => handleSelectProspect(prospect)}
                      >
                        {formatProspectName(prospect)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!searching &&
                debouncedSearchQuery.trim().length >= 2 &&
                searchResults.length === 0 &&
                !searchError && (
                  <div className="create-recruiting-note__no-results">
                    No prospects found
                  </div>
                )}
              <p className="create-recruiting-note__picker-hint">
                Leave blank to save an unassigned note.
              </p>
            </div>
          )}
        </label>

        <label className="create-recruiting-note__field">
          <span className="create-recruiting-note__field-label">Title (optional)</span>
          <input
            type="text"
            className="create-recruiting-note__input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={200}
            disabled={submitting}
          />
        </label>

        <label className="create-recruiting-note__field">
          <span className="create-recruiting-note__field-label">Note</span>
          <textarea
            className="create-recruiting-note__textarea"
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={10}
            placeholder="Write your note..."
            disabled={submitting}
            required
          />
        </label>

        {submitError && (
          <div className="create-recruiting-note__error">{submitError}</div>
        )}

        <div className="create-recruiting-note__actions">
          <button
            type="submit"
            className="create-recruiting-note__submit"
            disabled={!canSubmit}
          >
            {submitting ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </form>

      {savedNote && (
        <>
          <ConfirmModal
            isOpen={!notifyOpen}
            onClose={() => leaveAfterSave(savedNote.prospect)}
            onConfirm={() => setNotifyOpen(true)}
            title="Note saved"
            message={`Do you want to notify a coach about this note on ${formatProspectName(savedNote.prospect)}?`}
            confirmText="Notify a coach"
            cancelText="Not now"
          />
          <NotifyStaffModal
            isOpen={notifyOpen}
            prospectId={savedNote.prospect.id}
            itemType="note"
            itemId={savedNote.id}
            subject={formatProspectName(savedNote.prospect)}
            onClose={() => leaveAfterSave(savedNote.prospect)}
          />
        </>
      )}
    </div>
  );
};
